// src/pages/StoreCreditSettings.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
  Stack,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RedeemIcon from '@mui/icons-material/Redeem';
import SettingsHeader from '../components/SettingsHeader';
import StoreCreditDialog from '../components/StoreCreditDialog';
import {
  fetchStoreCreditOverview,
  type StoreCreditCustomer,
  type StoreCreditStats,
} from '../api/storeCreditApi';

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const EMPTY_STATS: StoreCreditStats = {
  total_outstanding: 0,
  customers_with_balance: 0,
  customers_total: 0,
  total_issued: 0,
  total_redeemed: 0,
};

const StatCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, hint, icon, color }) => {
  const theme = useTheme();
  return (
    <Card sx={{ boxShadow: theme.shadows[3], borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${color}22`,
              color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} noWrap>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {label}
            </Typography>
          </Box>
        </Stack>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const StoreCreditManagement: React.FC = () => {
  const theme = useTheme();

  const [stats, setStats] = useState<StoreCreditStats>(EMPTY_STATS);
  const [customers, setCustomers] = useState<StoreCreditCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [onlyPositive, setOnlyPositive] = useState(false);
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [pageSize, setPageSize] = useState(20);

  const [selected, setSelected] = useState<StoreCreditCustomer | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce the search box → commit to `search` (and reset to page 0).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchStoreCreditOverview({
        page: page + 1,
        pageSize,
        search,
        onlyPositive,
      });
      setStats(data.stats);
      setCustomers(data.customers);
      setTotal(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load store credit data.');
      setCustomers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, onlyPositive]);

  useEffect(() => {
    load();
  }, [load]);

  const openFor = (customer: StoreCreditCustomer) => {
    setSelected(customer);
    setDialogOpen(true);
  };

  // After editing credit in the dialog, refresh the list + stats.
  const handleDialogClose = () => {
    setDialogOpen(false);
    load();
  };

  const statCards = useMemo(
    () => [
      {
        label: 'Outstanding balance',
        value: money(stats.total_outstanding),
        hint: 'Total credit customers can still spend',
        icon: <AccountBalanceWalletIcon />,
        color: theme.palette.primary.main,
      },
      {
        label: 'Customers with credit',
        value: String(stats.customers_with_balance),
        hint: `${stats.customers_total} have ever held credit`,
        icon: <PeopleIcon />,
        color: theme.palette.info.main,
      },
      {
        label: 'Issued (lifetime)',
        value: money(stats.total_issued),
        hint: 'All credit ever added',
        icon: <TrendingUpIcon />,
        color: theme.palette.success.main,
      },
      {
        label: 'Redeemed (lifetime)',
        value: money(stats.total_redeemed),
        hint: 'Credit spent or deducted',
        icon: <RedeemIcon />,
        color: theme.palette.warning.main,
      },
    ],
    [stats, theme]
  );

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', p: 3 }}>
      <SettingsHeader
        title="Store Credit"
        subtitle="Track balances, add or adjust credit, and review the full audit trail"
        icon={<CreditCardIcon fontSize="large" />}
      />

      {/* Stat cards */}
      <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
        {statCards.map((s) => (
          <Grid item xs={12} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* Toolbar */}
      <Card sx={{ boxShadow: theme.shadows[3], borderRadius: 2, mt: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, email, or phone…"
              size="small"
              sx={{ width: { xs: '100%', md: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={onlyPositive}
                  onChange={(e) => {
                    setOnlyPositive(e.target.checked);
                    setPage(0);
                  }}
                />
              }
              label="Only customers with a balance"
            />
          </Stack>

          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          <TableContainer component={Paper} elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Issued</TableCell>
                  <TableCell align="right">Redeemed</TableCell>
                  <TableCell align="center">Txns</TableCell>
                  <TableCell>Last activity</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {search || onlyPositive
                          ? 'No customers match these filters.'
                          : 'No store credit has been issued yet.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {c.name || 'Unnamed customer'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.email || 'no email'}
                          {c.phone ? ` · ${c.phone}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight={700}
                          variant="body2"
                          color={c.balance > 0 ? 'success.main' : 'text.secondary'}
                        >
                          {money(c.balance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{money(c.total_granted)}</TableCell>
                      <TableCell align="right">{money(c.total_redeemed)}</TableCell>
                      <TableCell align="center">{c.tx_count}</TableCell>
                      <TableCell>
                        <Tooltip title={c.last_activity ? new Date(c.last_activity).toLocaleString() : ''}>
                          <span>{formatDate(c.last_activity)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CreditCardIcon />}
                          onClick={() => openFor(c)}
                        >
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </CardContent>
      </Card>

      <StoreCreditDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        customerId={selected?.id ?? null}
        customerName={selected?.name ?? undefined}
      />
    </Box>
  );
};

export default StoreCreditManagement;
